import { useState, useRef, useEffect } from "react";
import "./ThemeManager.css";

export default function ThemeManager({ 
  isOpen, 
  onClose, 
  currentTheme, 
  onThemeChange,
  conversationId 
}) {
  const [selectedTheme, setSelectedTheme] = useState(currentTheme || 'default');
  const [customImage, setCustomImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [opacity, setOpacity] = useState(0.3);
  const [blur, setBlur] = useState(0);
  const [activeTab, setActiveTab] = useState('presets'); // 'presets', 'gallery', 'upload'
  const fileInputRef = useRef(null);

  const presetThemes = [
    {
      id: 'default',
      name: 'Default',
      background: 'var(--bg-primary)',
      preview: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    {
      id: 'dark',
      name: 'Dark',
      background: '#1a1a1a',
      preview: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)'
    },
    {
      id: 'light',
      name: 'Light',
      background: '#ffffff',
      preview: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
    },
    {
      id: 'blue',
      name: 'Ocean Blue',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      preview: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    {
      id: 'green',
      name: 'Forest Green',
      background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      preview: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
    },
    {
      id: 'purple',
      name: 'Purple Haze',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      preview: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    {
      id: 'sunset',
      name: 'Sunset',
      background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 50%, #fecfef 100%)',
      preview: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 50%, #fecfef 100%)'
    },
    {
      id: 'custom',
      name: 'Custom Image',
      background: 'transparent',
      preview: 'linear-gradient(135deg, #ddd 0%, #ccc 100%)'
    }
  ];

  const cartoonImages = [
    { id: 'cartoon1', name: 'Cute Animals', url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop' },
    { id: 'cartoon2', name: 'Space Adventure', url: 'https://images.unsplash.com/photo-1534796636912-3b95b3ab5986?w=400&h=300&fit=crop' },
    { id: 'cartoon3', name: 'Underwater World', url: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=400&h=300&fit=crop' },
    { id: 'cartoon4', name: 'Forest Friends', url: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=300&fit=crop' },
    { id: 'cartoon5', name: 'City Lights', url: 'https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=400&h=300&fit=crop' },
    { id: 'cartoon6', name: 'Mountain View', url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop' }
  ];

  const flowerImages = [
    { id: 'flower1', name: 'Rose Garden', url: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=400&h=300&fit=crop' },
    { id: 'flower2', name: 'Cherry Blossoms', url: 'https://images.unsplash.com/photo-1522383225653-ed111181a951?w=400&h=300&fit=crop' },
    { id: 'flower3', name: 'Sunflowers', url: 'https://images.unsplash.com/photo-1597848212624-a19eb35e2651?w=400&h=300&fit=crop' },
    { id: 'flower4', name: 'Lavender Field', url: 'https://images.unsplash.com/photo-1499002238440-d264edd596ec?w=400&h=300&fit=crop' },
    { id: 'flower5', name: 'Tulip Garden', url: 'https://images.unsplash.com/photo-1526047932273-341f2a7631f9?w=400&h=300&fit=crop' },
    { id: 'flower6', name: 'Wild Flowers', url: 'https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=400&h=300&fit=crop' }
  ];

  const galleryImages = [
    { id: 'gallery1', name: 'Abstract Art', url: 'https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=400&h=300&fit=crop' },
    { id: 'gallery2', name: 'Minimalist', url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop' },
    { id: 'gallery3', name: 'Vintage', url: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=300&fit=crop' },
    { id: 'gallery4', name: 'Modern', url: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=400&h=300&fit=crop' },
    { id: 'gallery5', name: 'Nature', url: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=400&h=300&fit=crop' },
    { id: 'gallery6', name: 'Urban', url: 'https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=400&h=300&fit=crop' }
  ];

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleThemeSelect = (theme) => {
    setSelectedTheme(theme.id);
    if (theme.id !== 'custom') {
      onThemeChange({
        type: theme.id,
        background: theme.background,
        customImage: null,
        opacity: 0.3,
        blur: 0
      });
    }
  };

  const handleImageSelect = (image) => {
    setCustomImage(image.url);
    setSelectedTheme('custom');
    onThemeChange({
      type: 'custom',
      background: 'transparent',
      customImage: image.url,
      opacity: opacity,
      blur: blur
    });
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCustomImage(e.target.result);
        setImagePreview(e.target.result);
        setSelectedTheme('custom');
        onThemeChange({
          type: 'custom',
          background: 'transparent',
          customImage: e.target.result,
          opacity: opacity,
          blur: blur
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOpacityChange = (value) => {
    setOpacity(value);
    if (selectedTheme === 'custom' && customImage) {
      const newTheme = {
        type: 'custom',
        background: 'transparent',
        customImage: customImage,
        opacity: value,
        blur: blur
      };
      onThemeChange(newTheme);
    }
  };

  const handleBlurChange = (value) => {
    setBlur(value);
    if (selectedTheme === 'custom' && customImage) {
      const newTheme = {
        type: 'custom',
        background: 'transparent',
        customImage: customImage,
        opacity: opacity,
        blur: value
      };
      onThemeChange(newTheme);
    }
  };

  const handleRemoveImage = () => {
    setCustomImage(null);
    setImagePreview(null);
    setSelectedTheme('default');
    onThemeChange({
      type: 'default',
      background: 'var(--bg-primary)',
      customImage: null,
      opacity: 0.3,
      blur: 0
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSaveTheme = () => {
    const themeData = {
      conversationId,
      theme: {
        type: selectedTheme,
        background: selectedTheme === 'custom' ? 'transparent' : presetThemes.find(t => t.id === selectedTheme)?.background,
        customImage: selectedTheme === 'custom' ? customImage : null,
        opacity: opacity,
        blur: blur
      }
    };
    
    const savedThemes = JSON.parse(localStorage.getItem('messengerThemes') || '{}');
    savedThemes[conversationId] = themeData.theme;
    localStorage.setItem('messengerThemes', JSON.stringify(savedThemes));
    onClose();
  };

  const handleResetTheme = () => {
    setSelectedTheme('default');
    setCustomImage(null);
    setOpacity(0.3);
    setBlur(0);
    onThemeChange({
      type: 'default',
      background: 'var(--bg-primary)',
      customImage: null,
      opacity: 0.3,
      blur: 0
    });
    
    // Remove from localStorage
    const savedThemes = JSON.parse(localStorage.getItem('messengerThemes') || '{}');
    delete savedThemes[conversationId];
    localStorage.setItem('messengerThemes', JSON.stringify(savedThemes));
  };

  if (!isOpen) return null;

  return (
    <div className="theme_manager_overlay" onClick={onClose}>
      <div className="theme_manager_modal" onClick={(e) => e.stopPropagation()}>
        <div className="theme_manager_header">
          <h3>Chat Theme</h3>
          <button className="close_btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <div className="theme_manager_content">
          <div className="theme_preview">
            <h4>Preview</h4>
            <div 
              className="theme_preview_area"
              style={{
                background: selectedTheme === 'custom' && customImage 
                  ? `linear-gradient(rgba(0,0,0,${opacity}), rgba(0,0,0,${opacity})), url(${customImage})`
                  : presetThemes.find(t => t.id === selectedTheme)?.background || 'var(--bg-primary)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: selectedTheme === 'custom' && customImage ? `blur(${blur}px)` : 'none'
              }}
            >
              <div className="preview_message">
                <div className="preview_avatar"></div>
                <div className="preview_bubble">
                  <p>This is how your chat will look!</p>
                </div>
              </div>
            </div>
          </div>

          <div className="theme_tabs">
            <button 
              className={`tab_btn ${activeTab === 'presets' ? 'active' : ''}`}
              onClick={() => setActiveTab('presets')}
            >
              Preset Themes
            </button>
            <button 
              className={`tab_btn ${activeTab === 'cartoons' ? 'active' : ''}`}
              onClick={() => setActiveTab('cartoons')}
            >
              Cartoons
            </button>
            <button 
              className={`tab_btn ${activeTab === 'flowers' ? 'active' : ''}`}
              onClick={() => setActiveTab('flowers')}
            >
              Flowers
            </button>
            <button 
              className={`tab_btn ${activeTab === 'gallery' ? 'active' : ''}`}
              onClick={() => setActiveTab('gallery')}
            >
              Gallery
            </button>
            <button 
              className={`tab_btn ${activeTab === 'upload' ? 'active' : ''}`}
              onClick={() => setActiveTab('upload')}
            >
              Upload
            </button>
          </div>

          <div className="theme_options">
            {activeTab === 'presets' && (
              <>
                <h4>Choose Theme</h4>
                <div className="preset_themes">
                  {presetThemes.map((theme) => (
                    <button
                      key={theme.id}
                      className={`theme_option ${selectedTheme === theme.id ? 'selected' : ''}`}
                      onClick={() => handleThemeSelect(theme)}
                    >
                      <div 
                        className="theme_preview_thumb"
                        style={{ background: theme.background }}
                      ></div>
                      <span>{theme.name}</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {activeTab === 'cartoons' && (
              <>
                <h4>Cartoon Themes</h4>
                <div className="image_grid">
                  {cartoonImages.map((image) => (
                    <button
                      key={image.id}
                      className={`image_option ${customImage === image.url ? 'selected' : ''}`}
                      onClick={() => handleImageSelect(image)}
                    >
                      <img src={image.url} alt={image.name} />
                      <span>{image.name}</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {activeTab === 'flowers' && (
              <>
                <h4>Flower Themes</h4>
                <div className="image_grid">
                  {flowerImages.map((image) => (
                    <button
                      key={image.id}
                      className={`image_option ${customImage === image.url ? 'selected' : ''}`}
                      onClick={() => handleImageSelect(image)}
                    >
                      <img src={image.url} alt={image.name} />
                      <span>{image.name}</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {activeTab === 'gallery' && (
              <>
                <h4>Gallery Themes</h4>
                <div className="image_grid">
                  {galleryImages.map((image) => (
                    <button
                      key={image.id}
                      className={`image_option ${customImage === image.url ? 'selected' : ''}`}
                      onClick={() => handleImageSelect(image)}
                    >
                      <img src={image.url} alt={image.name} />
                      <span>{image.name}</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {activeTab === 'upload' && (
              <>
                <h4>Upload Custom Image</h4>
                <div className="custom_image_section">
                  <div className="image_upload_area">
                    {customImage ? (
                      <div className="image_preview_container">
                        <img 
                          src={customImage} 
                          alt="Custom background" 
                          className="uploaded_image_preview"
                        />
                        <button 
                          className="remove_image_btn"
                          onClick={handleRemoveImage}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <div 
                        className="upload_placeholder"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 7v2.99s-1.99.01-2 0V7h-3s.01-1.99 0-2h3V2h2v3h3v2h-3zm-3 4V8h-3V5H5c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-8h-3zM5 19l3-4 2 3 3-4 4 5H5z"/>
                        </svg>
                        <p>Click to upload image</p>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                    />
                  </div>

                  {imagePreview && (
                    <div className="image_settings">
                      <div className="setting_group">
                        <label>Opacity: {opacity}</label>
                        <input
                          type="range"
                          min="0"
                          max="0.8"
                          step="0.1"
                          value={opacity}
                          onChange={(e) => handleOpacityChange(parseFloat(e.target.value))}
                          className="slider"
                        />
                      </div>
                      <div className="setting_group">
                        <label>Blur: {blur}px</label>
                        <input
                          type="range"
                          min="0"
                          max="10"
                          step="1"
                          value={blur}
                          onChange={(e) => handleBlurChange(parseInt(e.target.value))}
                          className="slider"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="theme_manager_actions">
          <button className="reset_btn" onClick={handleResetTheme}>
            Reset to Default
          </button>
          <div className="action_buttons">
            <button className="cancel_btn" onClick={onClose}>
              Cancel
            </button>
            <button className="save_btn" onClick={handleSaveTheme}>
              Save Theme
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 